import { TrendUp, TrendDown, Trophy, Lightning, GitBranch } from "@phosphor-icons/react";
import { trendGood } from "./metricDefs";

const compareLabels = {
  recovery: ["Восстановление", "%"], hrv: ["HRV", "мс"], rhr: ["Пульс покоя", "уд/мин"],
  strain: ["Нагрузка", ""], sleep_h: ["Сон", "ч"], sleep_performance: ["Эфф. сна", "%"], kcal: ["Калории", "ккал"],
};

export function PeriodCompareCard({ compare, periodDays, onSelect }) {
  if (!compare?.length) return null;
  return (
    <div className="glass-card p-6" data-testid="period-compare-card">
      <h3 className="font-heading font-semibold mb-1">Сравнение периодов</h3>
      <p className="text-xs text-muted-foreground mb-4">
        {periodDays === 0
          ? "Вторая половина вашей истории против первой · нажмите для деталей"
          : `Последние ${periodDays} дней против предыдущих ${periodDays} · нажмите для деталей`}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {compare.map((c) => {
          const [label, unit] = compareLabels[c.key] || [c.key, ""];
          const dir = trendGood(c.key, c.delta_pct);
          const color = dir === "good" ? "text-[#00e676]" : dir === "bad" ? "text-[#ff3b30]" : "text-muted-foreground";
          return (
            <button key={c.key} onClick={() => onSelect?.(c.key)} data-testid={`compare-${c.key}`}
              className="text-left rounded-md p-2 -m-1 hover:bg-secondary/50 transition-colors">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
              <div className="font-heading text-lg font-bold num">{c.current}{unit && <span className="text-xs font-normal text-muted-foreground"> {unit}</span>}</div>
              <div className={`flex items-center gap-1 text-xs num ${color}`}>
                {c.delta_pct > 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
                {Math.abs(c.delta_pct).toFixed(0)}%
                <span className="text-muted-foreground">было {c.previous}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CorrelationsCard({ correlations, onSelect }) {
  if (!correlations?.length) return null;
  const top = correlations[0];
  return (
    <div className="glass-card p-6" data-testid="correlations-card">
      <div className="flex items-center gap-2 mb-1">
        <GitBranch size={18} weight="duotone" className="text-primary" />
        <h3 className="font-heading font-semibold">Что влияет на ваше восстановление</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Корреляции по вашим данным ({top.n} дней). Сильнее всего: <span className="text-foreground font-medium">{top.label.toLowerCase()}</span> ({top.strength} связь)
      </p>
      <div className="space-y-2.5">
        {correlations.map((c) => (
          <button key={c.key} onClick={() => onSelect?.(c.key)} data-testid={`correlation-${c.key}`}
            className="w-full text-left rounded-md p-1 -m-1 hover:bg-secondary/50 transition-colors">
            <div className="flex justify-between text-xs mb-1">
              <span>{c.label}</span>
              <span className={`num font-semibold ${c.r > 0 ? "text-[#00e676]" : "text-[#ff3b30]"}`}>
                {c.r > 0 ? "+" : ""}{c.r} · {c.strength}
              </span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden flex">
              <div className={`h-full rounded-full ${c.r > 0 ? "bg-[#00e676]" : "bg-[#ff3b30]"}`}
                style={{ width: `${Math.min(Math.abs(c.r) * 100, 100)}%` }} />
            </div>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
        Положительная связь (зелёная) — фактор улучшает восстановление, отрицательная (красная) — снижает. Это корреляция, а не гарантия причинности.
      </p>
    </div>
  );
}

export function RecordsCard({ records, onSelectDay }) {
  if (!records?.length) return null;
  return (
    <div className="glass-card p-6" data-testid="records-card">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={18} weight="duotone" className="text-[#ffea00]" />
        <h3 className="font-heading font-semibold">Личные рекорды периода</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Нажмите на рекорд, чтобы открыть этот день</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {records.map((r) => (
          <button key={r.key} onClick={() => onSelectDay?.(r.date)} data-testid={`record-${r.key}`}
            className="flex items-center gap-3 rounded-md bg-secondary/60 p-3 text-left hover:bg-secondary transition-colors">
            <Lightning size={16} weight="fill" className="text-[#ffea00] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{r.label}{r.extra ? ` · ${r.extra}` : ""}</div>
              <div className="text-[11px] text-muted-foreground num">{r.date.slice(8, 10)}.{r.date.slice(5, 7)}.{r.date.slice(0, 4)}</div>
            </div>
            <span className="num font-heading font-bold text-sm">{r.value}{r.unit ? ` ${r.unit}` : ""}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
